/**
 * Date and birthday utilities
 */

import { ageFromParts } from '../../utils/age.js';

let cachedDateFormat = null;

/**
 * Detect the user's preferred date format based on browser locale.
 * Returns 'MDY' (Month-Day-Year, US), 'DMY' (Day-Month-Year, most countries), or 'YMD' (Year-Month-Day, Asian countries)
 */
export function getLocaleDateFormat() {
    if (cachedDateFormat !== null) {
        return cachedDateFormat;
    }

    try {
        const locale = navigator.language || navigator.userLanguage || 'en-US';
        // Format a test date and check the order of components
        const testDate = new Date(2023, 11, 25); // Dec 25, 2023
        const formatted = testDate.toLocaleDateString(locale);

        // Check position of month (12), day (25), year (2023 or 23)
        const monthPos = formatted.search(/12/);
        const dayPos = formatted.search(/25/);
        const yearPos = formatted.search(/2023|23/);

        if (yearPos !== -1 && yearPos < monthPos && yearPos < dayPos) {
            cachedDateFormat = 'YMD'; // Year first (e.g., China, Japan, Korea)
        } else if (monthPos !== -1 && monthPos < dayPos) {
            cachedDateFormat = 'MDY'; // Month first (e.g., US)
        } else {
            cachedDateFormat = 'DMY'; // Day first (e.g., Europe, most of world)
        }
    } catch (e) {
        cachedDateFormat = 'DMY'; // Default to most common format
    }

    return cachedDateFormat;
}

export function getDayOptions() {
    return Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
}

export function getYearOptions() {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 100 }, (_, i) => String(currentYear - i));
}

export function calculateAge(day, month, year) {
    if (!day || !month || !year) return null;

    return ageFromParts(day, month, year);
}

/**
 * Minimum registration age used when the server has not supplied one.
 * Mirrors AgeContactService::MIN_REGISTRATION_AGE, which the wizard receives
 * through init({ minAge }) and which CreateAccountValidator enforces on submit.
 */
export const DEFAULT_MIN_AGE = 13;

/**
 * The bound actually applied. An unusable value falls back to the mirrored
 * constant rather than skipping the check, so a broken bound cannot widen the
 * gate. Callers must resolve through this before showing the bound to a user,
 * or the message will name a different number than the check used.
 */
export function resolveMinAge(minAge) {
    return Number.isFinite(minAge) && minAge > 0 ? minAge : DEFAULT_MIN_AGE;
}

export function isValidAge(day, month, year, minAge) {
    const age = calculateAge(day, month, year);

    if (age === null) {
        return false;
    }

    return age >= resolveMinAge(minAge);
}

export function formatBirthday(day, month, year) {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Whether the day exists in that month. The day select offers 1 to 31 for
 * every month, and Date rolls an impossible day into the next month.
 */
export function isRealDate(day, month, year) {
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

    return date.getUTCFullYear() === Number(year)
        && date.getUTCMonth() === Number(month) - 1
        && date.getUTCDate() === Number(day);
}

/**
 * The birthday as a long date in the site locale (`<html lang>`, browser
 * locale as fallback) for the confirm sheet. '' when incomplete or impossible.
 * Gregorian and UTC throughout: the selects are Gregorian, and local time
 * cannot shift the day.
 */
export function formatBirthdayLong(day, month, year) {
    if (!day || !month || !year || !isRealDate(day, month, year)) {
        return '';
    }

    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    const locale = document.documentElement.lang || navigator.language || 'en-US';

    try {
        return new Intl.DateTimeFormat(locale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            calendar: 'gregory',
            timeZone: 'UTC'
        }).format(date);
    } catch (e) {
        return formatBirthday(day, month, year);
    }
}
